import React, { useCallback, useRef, useState } from 'react';
import Icon from '../shared/Icon';
import { CardGrid } from '../shared/Card';
import { LIBRARY_TYPES, plural } from '../../../models/LibraryModel';
import { useLibraryViewModel } from '../../../viewModels/useLibraryViewModel';
import LibraryTabs from './LibraryTabs';
import LibraryToolbar from './LibraryToolbar';
import LibraryCard from './LibraryCard';
import LibraryList from './LibraryList';
import AddDocumentDialog from './AddDocumentDialog';
import ArchiveConfirmDialog from './ArchiveConfirmDialog';
import { useDocumentActions } from './useDocumentActions';
import './Library.css';

/**
 * Library (PRD-1605, design handoff DES-24): reference keys, SOPs and
 * narratives, one tab per type. Type is set on upload and is the tab, so
 * there is no "All" tab and no type filter.
 *
 * Four states show nothing and they are not the same thing. "No reference
 * keys yet" means the collection is empty; "No reference keys found" means
 * the question returned nothing and the material may still be there, so it
 * carries the way out. Search and filter reach the same no-result screen.
 *
 * PRD-1606: Add document (in the header and the empty state) and Retry.
 * PRD-1607: Archive (asks first) and Unarchive (straight away). Objects are
 * archived, never deleted.
 */
function Library({ model, onShowNotification }) {
  const vm = useLibraryViewModel(model);
  const searchRef = useRef(null);
  const [opener, setOpener] = useState(null); // the Add document button that opened the dialog
  const [addedId, setAddedId] = useState(null); // a new document sends the list back to page 1
  const headerAddRef = useRef(null);
  const rootRef = useRef(null);
  const docs = useDocumentActions({ vm, rootRef, fallbackRef: headerAddRef, onShowNotification });
  // Stable, so the dialog's Escape listener isn't re-added on every upload tick.
  const closeDialog = useCallback(() => setOpener(null), []);
  const { typeMeta, filters, visibleDocuments, scopeTotal } = vm;
  const count = visibleDocuments.length;

  // The link that was pressed no longer exists, so focus goes to search.
  const handleClear = () => {
    vm.clearSearchAndFilters();
    if (searchRef.current) searchRef.current.focus();
  };

  // The new document lands on its own tab (the model switches to it).
  const handleSave = (fields) => {
    const doc = vm.addDocument(fields);
    if (!doc) return;
    setAddedId(doc.id);
    setOpener(null);
    onShowNotification?.(`${LIBRARY_TYPES[doc.type].label} added.`, 'success');
  };

  const addButton = (ref) => (
    <button ref={ref} type="button" className="resui-btn resui-btn--primary" onClick={(e) => setOpener(e.currentTarget)}>
      Add document
    </button>
  );

  let body = null;
  if (!vm.typeHasDocuments) {
    body = (
      <div className="library-empty">
        <Icon name={typeMeta.icon} size={32} className="library-empty-icon" />
        <h2 className="library-empty-title">No {typeMeta.many} yet</h2>
        <p className="library-empty-note">{typeMeta.hint}</p>
        {addButton()}
      </div>
    );
  } else if (count && vm.view === 'grid') {
    body = (
      <CardGrid className="library-grid">
        {visibleDocuments.map((doc) => <LibraryCard key={doc.id} doc={doc} actions={docs.actions} />)}
      </CardGrid>
    );
  } else if (count) {
    body = (
      <LibraryList
        documents={visibleDocuments}
        label={typeMeta.plural}
        actions={docs.actions}
        sort={vm.listSort}
        onSortChange={vm.setListSort}
        resetKey={`${vm.type}|${filters.search}|${filters.visibility}|${filters.usage}|${addedId}`}
        focusId={docs.focusId}
      />
    );
  }

  return (
    <div className="library" ref={rootRef}>
      <div className="library-section-head">
        <LibraryTabs type={vm.type} onSelect={vm.selectType} />
        {addButton(headerAddRef)}
      </div>

      <LibraryToolbar
        filters={filters}
        view={vm.view}
        onFilterChange={vm.setFilter}
        onViewChange={vm.selectView}
        searchRef={searchRef}
      />

      {vm.typeHasDocuments && (
        <p className="library-count-line" aria-live="polite">
          {count ? (
            `${count} of ${scopeTotal} ${plural(scopeTotal, typeMeta.one, typeMeta.many)}${filters.search.trim() ? ' found' : ''}`
          ) : (
            <>
              No {typeMeta.many} found.{' '}
              <button type="button" className="resui-link" onClick={handleClear}>Clear search and filters</button>
            </>
          )}
        </p>
      )}

      {body}

      {opener && (
        <AddDocumentDialog
          draft={vm.draft}
          defaultType={vm.type}
          opener={opener}
          fallbackFocus={headerAddRef}
          onDraftChange={vm.setDraft}
          onSave={handleSave}
          onClose={closeDialog}
          onShowNotification={onShowNotification}
        />
      )}

      {docs.confirming && (
        <ArchiveConfirmDialog
          item={docs.confirming.doc}
          opener={docs.confirming.opener}
          fallbackFocus={headerAddRef}
          onConfirm={docs.confirmArchive}
          onClose={docs.cancelArchive}
        />
      )}
    </div>
  );
}

export default Library;
